import React, { useEffect, useState } from "react";
import _ from "underscore";
import { IPage } from "api/entities/page";
import {
  defaultShardRepositoryFields,
  IShardRepositoryRequestParams,
  IShardRepository,
} from "api/entities/shard-repository";
import { IShardClusterDetailsItem } from "api/entities/shard-cluster";
import { shardRepositoryService } from "api/services/shard-repository-service";
import { getDefaultRequestParams } from "redux/reducers/entity";
import { FormMode, formUtils, IFormField, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import TooltipQuestion from "components/shared-ui/tooltip-question";
import {
  IShardClusterRepository,
  ShardRepositoriesSection,
} from "../../../shard-clusters/sections/storage-nodes-section/shard-repositories-section";
import { Label } from "components/shared-ui/label";

export interface IPoolInfoSectionProps {
  className?: string;
  storagePool: IShardClusterDetailsItem;
}

interface IShardClusterRepositoriesForm {
  shardClusterRepositories: IFormField<IShardClusterRepository[]>;
}

interface IShardClusterRepositoriesFormValue {
  shardClusterRepositories: IShardClusterRepository[];
}

export function getStoragePoolNodes(
  shardCluster: IShardClusterDetailsItem,
  shardRepositoryPage: IPage<IShardRepository>,
) {
  let shardRepositoryDict = shardRepositoryPage.data.reduce(
    (acc, x) => ({ [x.id]: x, ...acc }),
    {},
  );

  const shardRepositories = _.unique(
    [
      ...shardCluster.contentStorageNodes,
      ...shardCluster.keyStorageNodes,
      ...shardCluster.metaStorageNodes,
    ],
    (n) => n.id,
  )
    .sort((x, y) => x.id.localeCompare(y.id))
    .map((x) => shardRepositoryDict[x.id]);

  const getTypes = (nodeId: string) => ({
    storeContent: shardCluster.contentStorageNodes.some((n) => n.id === nodeId),
    storeKey: shardCluster.keyStorageNodes.some((n) => n.id === nodeId),
    storeMetadata: shardCluster.metaStorageNodes.some((n) => n.id === nodeId),
  });

  return shardRepositories.some((x) => !!x)
    ? shardRepositories.map((shardRepository) => ({
        shardRepository,
        types: getTypes(shardRepository.id),
      }))
    : [];
}

const STORAGE_NODE_TOOLTIP = "Security Level / Redundancy Level";

export function PoolStorageNodesInfoSection(props: IPoolInfoSectionProps) {
  const { storagePool } = props;
  const [requestParams, setRequestParams] =
    useState<IShardRepositoryRequestParams>({
      ...getDefaultRequestParams(),
      fields: defaultShardRepositoryFields,
    });
  const [api] = useApi();

  const form: IShardClusterRepositoriesForm = {
    shardClusterRepositories: useField<IShardClusterRepository[]>([]),
  };

  useEffect(() => {
    if (!!storagePool) {
      const updatedRequestParams = {
        ...requestParams,
        pool: storagePool.id,
        pageSize: undefined,
      };
      setRequestParams(updatedRequestParams);

      api(shardRepositoryService.getPage(updatedRequestParams)).then(
        (shardRepositoryPage: IPage<IShardRepository>) => {
          let shardClusterRepositories = getStoragePoolNodes(
            storagePool,
            shardRepositoryPage,
          );
          formUtils.setFormValue<IShardClusterRepositoriesFormValue>(form, {
            shardClusterRepositories,
          });
        },
      );
    }
    // eslint-disable-next-line
  }, [storagePool]);

  return (
    storagePool && (
      <div className={props.className}>
        <ShardRepositoriesSection
          mode={FormMode.ReadOnly}
          shardClusterRepositories={form.shardClusterRepositories}
        />

        <Label
          label="Content"
          value={
            <>
              <span>
                {storagePool.contentSecurityLevel}/
                {storagePool.contentRedundancyLevel}
              </span>
              <TooltipQuestion>{STORAGE_NODE_TOOLTIP}</TooltipQuestion>
            </>
          }
        />
        <Label
          label="Key"
          value={
            <span>
              {storagePool.keySecurityLevel}/{storagePool.keyRedundancyLevel}
            </span>
          }
        />
        <Label
          label="Metada"
          value={
            <span>
              {storagePool.metaSecurityLevel}/{storagePool.metaRedundancyLevel}
            </span>
          }
        />
      </div>
    )
  );
}
